/**
 * React hook for tracking functionality
 * 
 * Provides convenience methods for tracking user interactions
 * with type safety and automatic event enrichment.
 * 
 * Requirements: 1.4, 1.5
 */

import { useContext } from 'react';
import { TrackingContext } from './TrackingProvider';
import { 
  TrackingEvent, 
  PageViewEvent, 
  ButtonClickEvent, 
  ErrorEvent, 
  FeatureUsedEvent 
} from '../types/events';
import { TrackingConfig } from '../types/config';

/**
 * Hook return type with all tracking methods
 */
export interface TrackingHook {
  /** Generic method to track any event with type safety */
  trackEvent: <T extends TrackingEvent>(event: T) => Promise<void>;
  /** Force track an event even if tracking is disabled - use only for critical events like SESSION_END on logout */
  forceTrackEvent: <T extends TrackingEvent>(event: T) => Promise<void>;
  /** Helper to track page view events */
  trackPageView: (pageName: string, metadata?: Record<string, any>) => void;
  /** Helper to track button click events */
  trackButtonClick: (buttonId: string, metadata?: Record<string, any>) => void;
  /** Helper to track error events */
  trackError: (error: Error, context?: Record<string, any>) => void;
  /** Helper to track feature usage events */
  trackFeatureUsed: (featureName: string, metadata?: Record<string, any>) => void;
  /** Flush all queued events immediately - call before logout */
  flushTracking: () => Promise<void>;
  /** Refresh configuration from backend */
  refreshConfig: () => Promise<void>;
  /** Whether tracking is currently enabled */
  isEnabled: boolean;
  /** Current tracking configuration */
  config: TrackingConfig | null;
}

/**
 * Generate current timestamp in ISO 8601 UTC format
 */
const getCurrentTimestamp = (): string => {
  return new Date().toISOString();
};

/**
 * Get the environment name mapped to backend expected values: dev, qa, staging, prod
 */
const getEnvironment = (): string => {
  // Check for explicit REACT_APP_ENVIRONMENT override first (set in .env files)
  const reactAppEnv = (process.env as any).REACT_APP_ENVIRONMENT;
  if (reactAppEnv && ['dev', 'qa', 'staging', 'prod'].includes(reactAppEnv)) {
    return reactAppEnv;
  }
  // Map NODE_ENV to backend values
  const nodeEnv = process.env.NODE_ENV || 'development';
  const envMap: Record<string, string> = {
    development: 'dev',
    test: 'dev',
    production: 'prod',
  };
  return envMap[nodeEnv] || 'dev';
};

/**
 * Generate session ID (in production, this should be persisted and reused)
 * For now, generate a simple UUID-like ID
 */
const getSessionId = (): string => {
  // Try to get from sessionStorage first
  if (typeof window !== 'undefined' && window.sessionStorage) {
    let sessionId = sessionStorage.getItem('tracking_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
      sessionStorage.setItem('tracking_session_id', sessionId);
    }
    return sessionId;
  }
  
  // Fallback to generating a new ID each time
  return `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
};

/**
 * Get page name from current location
 */
const getCurrentPageName = (): string => {
  if (typeof window !== 'undefined') {
    return window.location.pathname;
  }
  return 'unknown';
};

/**
 * useTracking hook
 * 
 * Exposes tracking functionality to React components.
 * Must be used within a TrackingProvider.
 * 
 * @throws Error if used outside of TrackingProvider
 * 
 * Requirements: 1.4, 1.5
 */
export const useTracking = (): TrackingHook => {
  const context = useContext(TrackingContext);

  if (!context) {
    throw new Error('useTracking must be used within a TrackingProvider');
  }

  const { queue, config, isEnabled, appName, userEnrichment, disabledEventTypes, refreshConfig, batchManager } = context;

  /**
   * Generic trackEvent method with type safety
   * 
   * Validates event, adds to queue, and triggers batch manager.
   * 
   * Requirement 1.4: trackEvent() with valid event adds to EventQueue
   * Requirement 1.5: trackEvent() with invalid event rejects and logs warning
   */
  const trackEvent = async <T extends TrackingEvent>(event: T): Promise<void> => {
    // Check if tracking is enabled
    if (!isEnabled) {
      console.debug('[useTracking] Tracking is disabled, event not queued');
      return;
    }

    // Check if this event type is explicitly disabled
    if (disabledEventTypes.includes(event.event_type)) {
      console.debug(`[useTracking] Event type "${event.event_type}" is disabled, event not queued`);
      return;
    }

    // Validate dependencies
    if (!queue) {
      console.warn('[useTracking] EventQueue not initialized, event not queued');
      return;
    }

    try {
      // Basic validation - ensure required fields are present
      if (!event.event_type || !event.timestamp || !event.session_id || !event.app_name) {
        throw new Error('Invalid event: missing required fields (event_type, timestamp, session_id, app_name)');
      }

      // Enrich event with user context (user_id_hash, customer_name, location_name, role_name)
      const enrichedEvent: T = {
        ...event,
        user_context: {
          ...event.user_context,
          user_id_hash: userEnrichment.user_id_hash || null,
          customer_name: userEnrichment.customer_name || null,
          location_name: userEnrichment.location_name || null,
          role_name: userEnrichment.role_name || null,
        },
      };

      // Enqueue the enriched event
      await queue.enqueue(enrichedEvent);
      console.debug(`[useTracking] Event queued: ${event.event_type}`);
    } catch (error) {
      console.warn('[useTracking] Failed to track event:', error);
    }
  };

  /**
   * Force track an event bypassing the isEnabled check.
   * Use only for critical events like SESSION_END on logout
   * where tracking may have been disabled as part of the logout flow.
   */
  const forceTrackEvent = async <T extends TrackingEvent>(event: T): Promise<void> => {
    if (!queue) {
      console.warn('[useTracking] EventQueue not initialized, event not queued');
      return;
    }
    try {
      const enrichedEvent: T = {
        ...event,
        user_context: {
          ...event.user_context,
          user_id_hash: userEnrichment.user_id_hash || null,
          customer_name: userEnrichment.customer_name || null,
          location_name: userEnrichment.location_name || null,
          role_name: userEnrichment.role_name || null,
        },
      };
      await queue.enqueue(enrichedEvent);
      console.debug(`[useTracking] Force queued: ${event.event_type}`);
    } catch (error) {
      console.warn('[useTracking] Failed to force track event:', error);
    }
  };

  /**
   * Helper method to track page view events
   * 
   * Automatically captures current page URL and referrer.
   * 
   * Requirement 1.4: Convenience method for PAGE_VIEW events
   */
  const trackPageView = (pageName: string, metadata?: Record<string, any>): void => {
    if (!isEnabled) {
      return;
    }

    try {
      const event: PageViewEvent = {
        event_type: 'PAGE_VIEW',
        timestamp: getCurrentTimestamp(),
        session_id: getSessionId(),
        app_name: appName,
        user_context: {
          environment: getEnvironment(),
        },
        page_name: pageName,
        page_url: typeof window !== 'undefined' ? window.location.href : '',
        referrer: typeof window !== 'undefined' ? document.referrer : undefined,
        metadata,
      };

      trackEvent(event);
    } catch (error) {
      console.warn('[useTracking] Failed to track page view:', error);
    }
  };

  /**
   * Helper method to track button click events
   * 
   * Captures button interaction with context.
   * 
   * Requirement 1.4: Convenience method for BUTTON_CLICK events
   */
  const trackButtonClick = (
    buttonId: string,
    metadata?: Record<string, any>
  ): void => {
    if (!isEnabled) {
      return;
    }

    try {
      const event: ButtonClickEvent = {
        event_type: 'BUTTON_CLICK',
        timestamp: getCurrentTimestamp(),
        session_id: getSessionId(),
        app_name: appName,
        user_context: {
          environment: getEnvironment(),
        },
        button_id: buttonId,
        page_name: getCurrentPageName(),
        metadata,
      };

      trackEvent(event);
    } catch (error) {
      console.warn('[useTracking] Failed to track button click:', error);
    }
  };

  /**
   * Helper method to track error events
   * 
   * Captures error with stack trace and context.
   * Stack trace is limited to 5000 characters as per design.
   * 
   * Requirement 1.4: Convenience method for ERROR events
   */
  const trackError = (error: Error, context?: Record<string, any>): void => {
    if (!isEnabled) {
      return;
    }

    try {
      // Limit stack trace to 5000 characters as per design
      const stackTrace = error.stack?.substring(0, 5000) || '';

      const event: ErrorEvent = {
        event_type: 'ERROR',
        timestamp: getCurrentTimestamp(),
        session_id: getSessionId(),
        app_name: appName,
        user_context: {
          environment: getEnvironment(),
        },
        error_name: error.name,
        error_message: error.message,
        stack_trace: stackTrace,
        source_url: typeof window !== 'undefined' ? window.location.href : '',
        component_name: context?.componentName,
        component_stack: context?.componentStack,
      };

      trackEvent(event);
    } catch (trackingError) {
      console.warn('[useTracking] Failed to track error:', trackingError);
    }
  };

  /**
   * Helper method to track feature usage events
   * 
   * Captures feature interaction with optional component context.
   * 
   * Requirement 1.4: Convenience method for FEATURE_USED events
   */
  const trackFeatureUsed = (
    featureName: string,
    metadata?: Record<string, any>
  ): void => {
    if (!isEnabled) {
      return;
    }

    try {
      const event: FeatureUsedEvent = {
        event_type: 'FEATURE_USED',
        timestamp: getCurrentTimestamp(),
        session_id: getSessionId(),
        app_name: appName,
        user_context: {
          environment: getEnvironment(),
        },
        feature_name: featureName,
        component_name: metadata?.componentName,
        page_name: getCurrentPageName(),
        metadata,
      };

      trackEvent(event);
    } catch (error) {
      console.warn('[useTracking] Failed to track feature usage:', error);
    }
  };

  /**
   * Flush all queued events immediately.
   * Call this before logout to ensure SESSION_END and other pending events are sent.
   */
  const flushTracking = async (): Promise<void> => {
    if (batchManager) {
      try {
        await batchManager.flushNow();
        console.debug('[useTracking] Tracking flushed before logout');
      } catch (error) {
        console.warn('[useTracking] Failed to flush tracking:', error);
      }
    }
  };

  /**
   * Return the tracking hook interface
   * 
   * Requirement 1.5: Return isEnabled and config state
   */
  return {
    trackEvent,
    forceTrackEvent,
    trackPageView,
    trackButtonClick,
    trackError,
    trackFeatureUsed,
    flushTracking,
    refreshConfig,
    isEnabled,
    config,
  };
};
